/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import ExportModal from "../../../components/ExportModal";
// @mui material components
import { styled } from "@mui/material/styles";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Autocomplete from "@mui/material/Autocomplete";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import FormField from "../../../layouts/ecommerce/products/new-product/components/FormField";
import TextField from "@mui/material/TextField";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";

// Material Dashboard 2 React Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDBadge from "components/MDBadge";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
/* import DataTable from "examples/Tables/DataTable"; */

import { setCurrentDevices } from "../../../redux/reducers/devicesReducer";
import { setCurrentOrder } from "../../../redux/reducers/ordersReducer";
import {
  setDeployed,
  setArea,
  setForeman,
  setResponsibleBusiness,
  setSalesman,
  setManagementAssistant,
  setSupplier,
  setPlan,
  setMaintenanceContract,
  setSafetyStudy,
  setCEControl,
  setUEDeclaration,
  setInternalControl,
  setStatus,
  setDeviceStatus,
  setWorkType,
  setValueBigFilter,
  setSelectBigFilter,
  setPage,
  setSortBy,
} from "../../../redux/reducers/filtersReducer";

import { getSpecificUsers } from "../../../api/users";

import { getOrders, getUserAreas } from "../../../api/orders";

import CreateEditOrder from "../../../customComponents/devicesComponents/CreateEditOrder";

import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";

import {
  areaEnum,
  areaColor,
  deviceStatusAdminEnum,
  workTypeEnum,
  planEnum,
  safetyStudyEnum,
  controlEnum,
  statusEnum,
  scndMaintenanceContractEnum,
  statusColor,
} from "../../../config/enum";

import { useDispatch, useSelector } from "react-redux";

import moment from "moment-timezone";

import { useAuth } from "context/AuthContext";
import useAuthCheck from "config/useAuthCheck";

// Options de tri
const sortOptions = [
  { label: "Dernière modification", value: { sortBy: "last_modified" } },
  { label: "Date de création", value: { sortBy: "created_date" } },
];

export default function Orders() {
  useAuthCheck();
  const [isLoading, setIsLoading] = useState(true);
  const [isSetupComplete, setIsSetupComplete] = useState(false);

  const allSuppliers = useSelector((state) => state.suppliers.allSuppliers);
  const deployed = useSelector((state) => state.filters.deployed);
  const area = useSelector((state) => state.filters.area);
  const foreman = useSelector((state) => state.filters.foreman);
  const responsibleBusiness = useSelector((state) => state.filters.responsibleBusiness);
  const salesman = useSelector((state) => state.filters.salesman);
  const managementAssistant = useSelector((state) => state.filters.managementAssistant);
  const supplier = useSelector((state) => state.filters.supplier);
  const plan = useSelector((state) => state.filters.plan);
  const maintenanceContract = useSelector((state) => state.filters.maintenanceContract);
  const safetyStudy = useSelector((state) => state.filters.safetyStudy);
  const CEControl = useSelector((state) => state.filters.CEControl);
  const UEDeclaration = useSelector((state) => state.filters.UEDeclaration);
  const internalControl = useSelector((state) => state.filters.internalControl);
  const status = useSelector((state) => state.filters.status);
  const deviceStatus = useSelector((state) => state.filters.deviceStatus);
  const workType = useSelector((state) => state.filters.workType);
  const valueBigFilter = useSelector((state) => state.filters.valueBigFilter);
  const selectBigFilter = useSelector((state) => state.filters.selectBigFilter);
  const page = useSelector((state) => state.filters.page);
  const sortBy = useSelector((state) => state.filters.sortBy);

  const [orders, setOrders] = useState(null);
  const [responsibles, setResponsibles] = useState([]);
  const [foremans, setForemans] = useState([]);
  const [salesmans, setSalesmans] = useState([]);
  const [managementAssistants, setManagementAssistants] = useState([]);

  const [totalPages, setTotalPages] = useState(0);
  const [availableAreas, setAvailableAreas] = useState([]);
  const myOrders = useSelector((state) => state.filters.myOrders);
  const { user } = useAuth();

  const handlePageChange = (event, value) => {
    dispatch(setPage(value));
  };

  const [controller] = useMaterialUIController();
  const navigate = useNavigate();
  const { darkMode, cardBoard } = controller;

  const bigFilterInfo = {
    "Numéro de commande": "tblorder.order_number",
    "Numéro d'appareil": "tbldevice.device_number",
    "Référence fabricant": "tblref.ref",
    "Numéro OT": "tbldevice.ot_number",
    Ville: "tblorder.city",
    "Maître d'ouvrage": "tblorder.building_owner",
    "Maître d'œuvre": "tblorder.project_manager",
    "Nom de chantier": "tblorder.site_name",
    "Adresse précise": "tbldevice.adress",
  };

  const [query, setQuery] = useState({});

  const dispatch = useDispatch();

  async function fetchResponsibles() {
    const response = await getSpecificUsers("responsibles");
    setResponsibles(response.data);
  }

  async function fetchForemans() {
    const response = await getSpecificUsers("foremans");
    setForemans(response.data);
  }

  async function fetchSalesmans() {
    const response = await getSpecificUsers("salesmans");
    setSalesmans(response.data);
  }

  async function fetchManagementAssistant() {
    const response = await getSpecificUsers("assistants");
    setManagementAssistants(response.data);
  }

  function formatTimestamp(timestamp) {
    // Parse the timestamp and convert it to the 'Europe/Paris' time zone
    const date = moment.utc(timestamp).tz("Europe/Paris");

    // Format the date and time
    return date.format("DD/MM/YYYY [à] HH:mm:ss");
  }

  useEffect(() => {
    fetchResponsibles();
    fetchForemans();
    fetchSalesmans();
    fetchManagementAssistant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user && user.role === "Contremaître") {
      getUserAreas(user.user_id).then((response) => {
        setAvailableAreas(response.data);
      });
    }
  }, [user]);

  function updateQuery(key, value) {
    if (value === null)
      setQuery((query) => {
        const copy = { ...query };
        delete copy[key];
        return copy;
      });
    else setQuery((query) => ({ ...query, [key]: value }));
  }

  // Combined useEffect for updating query based on filters and fetching orders
  const updateQueryBasedOnFilters = () => {
    let updatedQuery = {};
    if (area) updatedQuery["area"] = area;
    if (foreman) updatedQuery["foreman"] = foreman?.id || null;
    if (responsibleBusiness) updatedQuery["responsible_business"] = responsibleBusiness?.id || null;
    if (salesman) updatedQuery["salesman"] = salesman?.id || null;
    if (managementAssistant) updatedQuery["management_assistant"] = managementAssistant?.id || null;
    if (supplier) updatedQuery["tblref.supplier_id"] = supplier?.id || null;
    if (plan) {
      if (plan === "Non approuvés") {
        updatedQuery["tbldevice.plan_validation_date"] = "NULL";
      } else if (plan === "Approuvés") {
        updatedQuery["tbldevice.plan_validation_date"] = "NOT NULL";
      } else {
        updatedQuery["tbldevice.plan_validation_date"] = plan;
      }
    }
    if (safetyStudy) {
      if (safetyStudy === "Non faite") {
        updatedQuery["tbldevice.safety_study_date"] = "NULL";
      } else if (safetyStudy === "Faite") {
        updatedQuery["tbldevice.safety_study_date"] = "NOT NULL";
      } else {
        updatedQuery["tbldevice.safety_study_date"] = safetyStudy;
      }
    }
    if (CEControl) {
      if (CEControl === "Non fait") {
        updatedQuery["tbldevice.ce_control_date"] = "NULL";
      } else if (CEControl === "Fait") {
        updatedQuery["tbldevice.ce_control_date"] = "NOT NULL";
      } else {
        updatedQuery["tbldevice.ce_control_date"] = CEControl;
      }
    }
    if (UEDeclaration) {
      if (UEDeclaration === "Non faite") {
        updatedQuery["tbldevice.ue_declaration_filename"] = "NULL";
      } else if (UEDeclaration === "Faite") {
        updatedQuery["tbldevice.ue_declaration_filename"] = "NOT NULL";
      } else {
        updatedQuery["tbldevice.ue_declaration_filename"] = UEDeclaration;
      }
    }
    if (internalControl) {
      if (internalControl === "Non fait") {
        updatedQuery["tbldevice.internal_control_date"] = "NULL";
      } else if (internalControl === "Fait") {
        updatedQuery["tbldevice.internal_control_date"] = "NOT NULL";
      } else {
        updatedQuery["tbldevice.internal_control_date"] = internalControl;
      }
    }
    if (maintenanceContract) {
      if (maintenanceContract === "Non fait") {
        updatedQuery["tbldevice.maintenance_contract"] = "NULL";
      } else if (maintenanceContract === "Fait (tout au-dessus)") {
        updatedQuery["tbldevice.maintenance_contract"] = "NOT NULL";
      } else if (
        maintenanceContract === "Acceptation" ||
        maintenanceContract === "Refus" ||
        maintenanceContract === "Continuité" ||
        maintenanceContract === null
      ) {
        updateQuery("tbldevice.maintenance_contract", maintenanceContract);
      }
    }
    if (status) updatedQuery["tblfinance.status"] = status;
    if (deviceStatus) updatedQuery["tbldevice.status"] = deviceStatus;
    if (workType) {
      updatedQuery["work_type"] = workType;
    }
    if (selectBigFilter) {
      updatedQuery[bigFilterInfo[selectBigFilter]] = encodeURIComponent(valueBigFilter);
    }

    if (sortBy) updatedQuery["sortBy"] = sortBy;

    return updatedQuery;
  };

  const fetchOrdersWithFiltersAndPage = async (additionalQuery = {}) => {
    if (
      user &&
      user.role !== "Admin" &&
      user.role !== "Direction" &&
      user.role !== "Chef d'agence" &&
      user.role !== "Pilote" &&
      user.role !== "Contremaître" &&
      user.role !== "Commercial" &&
      user.role !== "Assistante de gestion"
    ) {
      navigate("/accueil");
    } else {
      try {
        const filtersQuery = updateQueryBasedOnFilters();
        if (!myOrders) {
          filtersQuery["allOrders"] = "true";
        }
        const response = await getOrders({ ...filtersQuery, ...query, ...additionalQuery }, page);
        setOrders(response.data.orders);
        setTotalPages(response.data.totalPages);
      } catch (error) {
        // Handle any errors appropriately
        console.error("Error fetching orders:", error);
      } finally {
        setIsLoading(false); // End loading regardless of success or failure
      }
    }
  };

  useEffect(() => {
    const setupAndFetchOrders = async () => {
      if (user && user.role === "Chef d'agence" && user.user_areas && user.user_areas.length === 1 && !area) {
        dispatch(setArea(user.user_areas[0]));
        updateQuery("area", user.user_areas[0]);
        dispatch(setPage(1));
      }

      if (user) {
        setIsSetupComplete(true);
      }
    };

    setupAndFetchOrders();
  }, [user, foreman, area, salesman, responsibleBusiness, dispatch]);

  useEffect(() => {
    if (isSetupComplete) {
      const fetchOrders = async () => {
        await fetchOrdersWithFiltersAndPage({});
        dispatch(setCurrentDevices([]));
        dispatch(setCurrentOrder(null));
      };

      fetchOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSetupComplete,
    area,
    foreman,
    responsibleBusiness,
    salesman,
    managementAssistant,
    supplier,
    plan,
    maintenanceContract,
    safetyStudy,
    UEDeclaration,
    CEControl,
    internalControl,
    status,
    deviceStatus,
    workType,
    valueBigFilter,
    selectBigFilter,
    page,
    sortBy,
    myOrders,
    dispatch,
  ]);

  const HtmlTooltip = styled(({ className, darkMode, ...props }) => (
    <Tooltip key={darkMode} {...props} classes={{ popper: className }} />
  ))(({ theme, darkMode }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      maxWidth: 220,
      background: darkMode ? "white" : "linear-gradient(195deg, #364c70, #273956)",
      boxShadow: darkMode ? "0 0.5px 2px 0.5px white" : "0 0.5px 2px 0.5px #344767",
      fontSize: 12,
    },
    [`& .MuiTooltip-arrow`]: {
      color: darkMode ? "white" : "#273956",
    },
  }));

  console.log(user);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={1.5}>
        <Grid container justifyContent="center" direction="row" spacing={2}>
          <Grid
            item
            xl={deployed ? 2.5 : 3.9}
            md={4}
            sm={8}
            xs={11}
            sx={{
              border: "1px solid lightGrey",
              borderRadius: "8px",
              marginTop: 2,
              marginLeft: 2,
              padding: "1rem !important",
            }}
          >
            <MDTypography mb={1} variant="subtitle1" align="center">
              Filtre de précision
            </MDTypography>
            <Grid xs={12} justifyContent="center" direction="row" spacing={2} item container>
              <Grid item xl={deployed ? 12 : 6} xs={12}>
                <Tooltip
                  title={selectBigFilter ? "" : "Veuillez sélectionner un élément à cibler"}
                  placement="top"
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={valueBigFilter}
                    onChange={(event) => {
                      dispatch(setValueBigFilter(event.target.value));
                      updateQuery(bigFilterInfo[selectBigFilter], event.target.value);
                      dispatch(setPage(1));
                    }}
                    inputProps={{
                      style: {
                        height: 29.13,
                      },
                    }}
                    type="text"
                    label="Valeur à rechercher"
                    disabled={!selectBigFilter}
                  />
                </Tooltip>
              </Grid>

              <Grid item xl={deployed ? 12 : 6} xs={12}>
                <Autocomplete
                  options={Object.keys(bigFilterInfo)}
                  value={selectBigFilter}
                  onChange={(event, newBigFilter) => {
                    if (newBigFilter) {
                      // If a new filter is selected, update the query and keep the current input value
                      dispatch(setSelectBigFilter(newBigFilter));
                    } else {
                      // If the filter is cleared, remove the corresponding query part and clear the input
                      setQuery((currentQuery) => {
                        const newQuery = { ...currentQuery };
                        Object.values(bigFilterInfo).forEach((filterKey) => {
                          if (filterKey in newQuery) {
                            delete newQuery[filterKey];
                          }
                        });
                        return newQuery;
                      });
                      dispatch(setSelectBigFilter(null));
                      dispatch(setValueBigFilter(""));
                    }
                    dispatch(setPage(1));
                  }}
                  renderInput={(params) => (
                    <MDInput pb={0} {...params} label="Élément à cibler" variant="outlined" />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid
            container
            item
            direction="column"
            justifyContent="top"
            spacing={2}
            xl={deployed ? 9.1 : 7.7}
            md={8}
            xs={10.5}
            sx={{
              padding: "1rem !important",
              border: "1px solid lightGrey",
              borderRadius: "8px",
              textAlign: "center",
              marginTop: 2,
              marginLeft: 2,
            }}
          >
            <Grid direction="row" spacing={2} container justifyContent="center">
              <Grid item xs={11.25}>
                <MDTypography mb={1} variant="subtitle1" align="center">
                  Filtres normaux
                </MDTypography>
              </Grid>
              <Grid item xs={0.75}>
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  style={{ height: 30 }}
                  onClick={() => {
                    dispatch(setDeployed(!deployed));
                  }}
                >
                  <Icon fontSize="medium">{deployed ? "expand_less" : "expand_more"}</Icon>
                </IconButton>
              </Grid>
            </Grid>
            <Grid direction="row" spacing={2} container justifyContent="center">
              <Grid item xl={deployed ? 2.4 : 3} lg={deployed ? 4 : 6} md={6} sm={6} xs={12}>
                <Autocomplete
                  options={deviceStatusAdminEnum}
                  value={deviceStatus}
                  onChange={(event, newDeviceStatus) => {
                    dispatch(setDeviceStatus(newDeviceStatus));
                    updateQuery("tbldevice.status", newDeviceStatus);
                    dispatch(setPage(1));
                  }}
                  renderInput={(params) => (
                    <TextField pb={0} {...params} label="Statut d'appareil" variant="outlined" />
                  )}
                />
              </Grid>
              <Grid item xl={deployed ? 2.4 : 3} lg={deployed ? 4 : 6} md={6} sm={6} xs={12}>
                <Tooltip
                  title={
                    user && user.role === "Chef d'agence"
                      ? "Ce filtre est paramétré par défaut selon votre rôle"
                      : ""
                  }
                  placement="top"
                >
                  <Autocomplete
                    key={area}
                    options={user && ["Admin", "Pilote", "Direction"].includes(user.role) ? areaEnum : (user && user.role === "Contremaître" ? availableAreas : (user && user.user_areas && user.user_areas.length > 0 ? user.user_areas : areaEnum))}
                    value={area}
                    onChange={(event, newArea) => {
                      dispatch(setArea(newArea));
                      updateQuery("area", newArea);
                      dispatch(setPage(1));
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Zone géographique" variant="outlined" />
                    )}
                    renderOption={(props, option) => (
                      <MDBox
                        component="li"
                        {...props}
                        style={{ color: areaColor[option], fontWeight: "bold" }}
                      >
                        {option}
                      </MDBox>
                    )}
                  />
                </Tooltip>
              </Grid>
              <Grid item xl={deployed ? 2.4 : 3} lg={deployed ? 4 : 6} md={6} sm={6} xs={12}>
                <Tooltip
                  title={
                    user && user.role === "Contremaître"
                      ? "Ce filtre est paramétré par défaut selon votre rôle"
                      : ""
                  }
                  placement="top"
                >
                  <Autocomplete
                    options={foremans}
                    disabled={false}
                    value={foreman}
                    onChange={(event, newForeman) => {
                      dispatch(setForeman(newForeman));
                      updateQuery("foreman", newForeman?.id || null);
                      dispatch(setPage(1));
                    }}
                    renderInput={(params) => (
                      <TextField pb={0} {...params} label="Contremaître" variant="outlined" />
                    )}
                  />
                </Tooltip>
              </Grid>
              <Grid item xl={deployed ? 2.4 : 3} lg={deployed ? 4 : 6} md={6} sm={6} xs={12}>
                <Autocomplete
                  options={managementAssistants}
                  value={managementAssistant}
                  onChange={(event, newManagementAssistant) => {
                    dispatch(setManagementAssistant(newManagementAssistant));
                    updateQuery("management_assistant", newManagementAssistant?.id || null);
                    dispatch(setPage(1));
                  }}
                  renderInput={(params) => (
                    <TextField
                      pb={0}
                      {...params}
                      label="Assistante de gestion"
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
              {deployed && (
                <Grid item xl={2.4} lg={4} md={6} sm={6} xs={12}>
                  <Autocomplete
                    options={workTypeEnum}
                    value={workType}
                    onChange={(event, newWorkType) => {
                      dispatch(setWorkType(newWorkType));
                      updateQuery("work_type", newWorkType);
                      dispatch(setPage(1));
                    }}
                    renderInput={(params) => (
                      <TextField pb={0} {...params} label="Type de travaux" variant="outlined" />
                    )}
                  />
                </Grid>
              )}
              {deployed && (
                <Grid item xl={2.4} lg={4} md={6} sm={6} xs={12}>
                  <Autocomplete
                    options={planEnum}
                    value={plan}
                    onChange={(event, newPlan) => {
                      dispatch(setPlan(newPlan));
                      if (newPlan === "Non approuvés") {
                        updateQuery("tbldevice.plan_validation_date", "NULL");
                      } else if (newPlan === "Approuvés") {
                        updateQuery("tbldevice.plan_validation_date", "NOT NULL");
                      }
                      if (newPlan == null) {
                        updateQuery("tbldevice.plan_validation_date", newPlan);
                      }
                      dispatch(setPage(1));
                    }}
                    renderInput={(params) => (
                      <TextField pb={0} {...params} label="Plan" variant="outlined" />
                    )}
                  />
                </Grid>
              )}
              {deployed && (
                <Grid item xl={2.4} lg={4} md={6} sm={6} xs={12}>
                  <Autocomplete
                    options={safetyStudyEnum}
                    value={safetyStudy}
                    onChange={(event, newSafetyStudy) => {
                      dispatch(setSafetyStudy(newSafetyStudy));
                      if (newSafetyStudy === "Non faite") {
                        updateQuery("tbldevice.internal_control_date", "NULL");
                      } else if (newSafetyStudy === "Faite") {
                        updateQuery("tbldevice.internal_control_date", "NOT NULL");
                      }
                      if (newSafetyStudy == null) {
                        updateQuery("tbldevice.internal_control_date", newSafetyStudy);
                      }
                      dispatch(setPage(1));
                    }}
                    renderInput={(params) => (
                      <TextField pb={0} {...params} label="Étude de sécurité" variant="outlined" />
                    )}
                  />
                </Grid>
              )}
              {deployed && (
                <Grid item xl={2.4} lg={4} md={6} sm={6} xs={12}>
                  <Autocomplete
                    options={controlEnum}
                    value={CEControl}
                    onChange={(event, newCEControl) => {
                      dispatch(setCEControl(newCEControl));
                      if (newCEControl === "Non fait") {
                        updateQuery("tbldevice.ce_control_date", "NULL");
                      } else if (newCEControl === "Fait") {
                        updateQuery("tbldevice.ce_control_date", "NOT NULL");
                      }
                      if (newCEControl == null) {
                        updateQuery("tbldevice.ce_control_date", newCEControl);
                      }
                      dispatch(setPage(1));
                    }}
                    renderInput={(params) => (
                      <TextField pb={0} {...params} label="Contrôle CE" variant="outlined" />
                    )}
                  />
                </Grid>
              )}
              {deployed && (
                <Grid item xl={2.4} lg={4} md={6} sm={6} xs={12}>
                  <Autocomplete
                    options={safetyStudyEnum}
                    value={UEDeclaration}
                    onChange={(event, newUEDeclaration) => {
                      dispatch(setUEDeclaration(newUEDeclaration));
                      if (newUEDeclaration === "Non faite") {
                        updateQuery("tbldevice.ue_declaration_filename", "NULL");
                      } else if (newUEDeclaration === "Faite") {
                        updateQuery("tbldevice.ue_declaration_filename", "NOT NULL");
                      }
                      if (newUEDeclaration == null) {
                        updateQuery("tbldevice.ue_declaration_filename", newUEDeclaration);
                      }
                      dispatch(setPage(1));
                    }}
                    renderInput={(params) => (
                      <TextField pb={0} {...params} label="Déclaration UE" variant="outlined" />
                    )}
                  />
                </Grid>
              )}
              {deployed && (
                <Grid item xl={2.4} lg={4} md={6} sm={6} xs={12}>
                  <Autocomplete
                    options={controlEnum}
                    value={internalControl}
                    onChange={(event, newInternalControl) => {
                      dispatch(setInternalControl(newInternalControl));
                      if (newInternalControl === "Non fait") {
                        updateQuery("tbldevice.internal_control_date", "NULL");
                      } else if (newInternalControl === "Fait") {
                        updateQuery("tbldevice.internal_control_date", "NOT NULL");
                      }
                      if (newInternalControl == null) {
                        updateQuery("tbldevice.internal_control_date", newInternalControl);
                      }
                      dispatch(setPage(1));
                    }}
                    renderInput={(params) => (
                      <TextField pb={0} {...params} label="Contrôle interne" variant="outlined" />
                    )}
                  />
                </Grid>
              )}
              {deployed && (
                <Grid item xl={2.4} lg={4} md={6} sm={6} xs={12}>
                  <Autocomplete
                    options={scndMaintenanceContractEnum}
                    value={maintenanceContract}
                    onChange={(event, newMaintenanceContract) => {
                      dispatch(setMaintenanceContract(newMaintenanceContract));
                      if (newMaintenanceContract === "Non fait") {
                        updateQuery("tbldevice.maintenance_contract", "NULL");
                      } else if (newMaintenanceContract === "Fait (tout au-dessus)") {
                        updateQuery("tbldevice.maintenance_contract", "NOT NULL");
                      } else if (
                        newMaintenanceContract === "Acceptation" ||
                        newMaintenanceContract === "Refus" ||
                        newMaintenanceContract === "Continuité" ||
                        newMaintenanceContract === null
                      ) {
                        updateQuery("tbldevice.maintenance_contract", newMaintenanceContract);
                      }
                      dispatch(setPage(1));
                    }}
                    renderInput={(params) => (
                      <TextField
                        pb={0}
                        {...params}
                        label="Contrat de maintenance"
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
              )}
              {deployed && (
                <Grid item xl={2.4} lg={4} md={6} sm={6} xs={12}>
                  <Autocomplete
                    options={statusEnum}
                    value={status}
                    onChange={(event, newStatus) => {
                      dispatch(setStatus(newStatus));
                      updateQuery("tblfinance.status", newStatus);
                      dispatch(setPage(1));
                    }}
                    renderInput={(params) => (
                      <TextField pb={0} {...params} label="Factures" variant="outlined" />
                    )}
                  />
                </Grid>
              )}
              {deployed && (
                <Grid item xl={2.4} lg={4} md={6} sm={6} xs={12}>
                  <Autocomplete
                    options={allSuppliers}
                    value={supplier}
                    onChange={(event, newSupplier) => {
                      dispatch(setSupplier(newSupplier));
                      updateQuery("tblref.supplier_id", newSupplier?.id || null);
                      dispatch(setPage(1));
                    }}
                    renderInput={(params) => (
                      <TextField pb={0} {...params} label="Fournisseur" variant="outlined" />
                    )}
                  />
                </Grid>
              )}
              {deployed && (
                <Grid item xl={2.4} lg={4} md={6} sm={6} xs={12}>
                  <Tooltip
                    title={
                      user && user.role === "Commercial"
                        ? "Ce filtre est paramétré par défaut selon votre rôle"
                        : ""
                    }
                    placement="top"
                  >
                    <Autocomplete
                      options={responsibles}
                      disabled={user && user.role === "Commercial"}
                      value={responsibleBusiness}
                      onChange={(event, newResponsibleBusiness) => {
                        dispatch(setResponsibleBusiness(newResponsibleBusiness));
                        updateQuery("responsible_business", newResponsibleBusiness?.id || null);
                        dispatch(setPage(1));
                      }}
                      renderInput={(params) => (
                        <TextField
                          pb={0}
                          {...params}
                          label="Commercial projet"
                          variant="outlined"
                        />
                      )}
                    />
                  </Tooltip>
                </Grid>
              )}
              {deployed && (
                <Grid item xl={2.4} lg={4} md={6} sm={6} xs={12}>
                  <Tooltip
                    title={
                      user && user.role === "Commercial"
                        ? "Ce filtre est paramétré par défaut selon votre rôle"
                        : ""
                    }
                    placement="top"
                  >
                    <Autocomplete
                      options={salesmans}
                      disabled={user && user.role === "Commercial"}
                      value={salesman}
                      onChange={(event, newSalesman) => {
                        dispatch(setSalesman(newSalesman));
                        updateQuery("salesman", newSalesman?.id || null);
                        dispatch(setPage(1));
                      }}
                      renderInput={(params) => (
                        <TextField
                          pb={0}
                          {...params}
                          label="Commercial service"
                          variant="outlined"
                        />
                      )}
                    />
                  </Tooltip>
                </Grid>
              )}
              {deployed && (
                <Grid item xl={2.4} lg={4} md={6} sm={6} xs={12}>
                  <Autocomplete
                    options={sortOptions}
                    value={
                      sortOptions.find((option) => option.value.sortBy === sortBy) || sortOptions[0]
                    }
                    onChange={(event, newSort) => {
                      if (newSort) {
                        dispatch(setSortBy(newSort.value.sortBy));
                        dispatch(setPage(1));
                      }
                    }}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField {...params} label="Trier par" variant="outlined" />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid alignItems="center" item container sx={{ padding: 1, marginTop: 3 }} xs={12}>
            {user &&
              (user.role === "Contremaître" ||
                user.role === "Chef d'agence" ||
                user.role === "Admin" ||
                user.role === "Pilote" ||
                user.role === "Assistante de gestion") && (
                <>
                  <Grid item xs={4}></Grid>
                  <Grid item xs={4} container justifyContent="center">
                    <CreateEditOrder edit={false} />
                  </Grid>
                  <Grid item xs={4} container justifyContent="flex-end" sx={{ paddingRight: 4 }}>
                    <ExportModal />
                  </Grid>
                </>
              )}
          </Grid>
        </Grid>
      </MDBox>

      <MDBox pt={6} pb={3}>
        <Grid container rowSpacing={5.5} columnSpacing={4}>
          {isLoading ? (
            <Grid item xs={12} justifyContent="center">
              <MDTypography pb={2} variant="h5" textAlign="center">
                Chargement des commandes...
              </MDTypography>
            </Grid>
          ) : orders && orders.length > 0 ? (
            !cardBoard ? (
              orders.map((order) => (
                <Grid key={order.order_id} item mb={1.5} xl={3} lg={4} md={6} xs={12}>
                  <Card sx={{ height: "100%", padding: "0 1rem" }}>
                    <MDBox
                      mx={0.5}
                      mt={-3}
                      py={3}
                      px={2}
                      style={{
                        backgroundColor: order && areaColor[order.area],
                        boxShadow: `0 1px 6px 1px ${order && areaColor[order.area]}`,
                      }}
                      variant="gradient"
                      borderRadius="lg"
                    >
                      <MDTypography variant="h5" textAlign="center" color="white">
                        Commande {order.order_number ? order.order_number : "inconnu"}
                      </MDTypography>
                    </MDBox>
                    <Grid textAlign="center" pt={3} sx={{ height: "100%" }} item container>
                      <Grid item xs={12}>
                        <MDTypography
                          sx={{ opacity: "0.7" }}
                          fontSize="0.75rem"
                          textTransform="uppercase"
                          textAlign="center"
                        >
                          Dernière modification
                        </MDTypography>
                        <MDTypography mt={-0.7} variant="h6" textAlign="center">
                          {order.last_edits.lastEdit
                            ? formatTimestamp(order.last_edits.lastEdit)
                            : "Date et heure inconnue"}
                        </MDTypography>
                        <MDTypography
                          mt={-0.5}
                          sx={{ opacity: "0.7" }}
                          fontSize="0.75rem"
                          textTransform="uppercase"
                          textAlign="center"
                        >
                          Par{" "}
                          <b>
                            {order.last_edits.userName !== null
                              ? order.last_edits.userName
                              : "utilisateur inconnu"}
                          </b>
                        </MDTypography>
                      </Grid>
                      <Grid item xs={12}>
                        <MDTypography
                          mt={1.5}
                          sx={{ opacity: "0.7" }}
                          fontSize="0.75rem"
                          textTransform="uppercase"
                          textAlign="center"
                        >
                          Appareils de la commande
                        </MDTypography>
                        <MDTypography mt={-0.7} variant="h6" textAlign="center">
                          {order.devices_info !== null && order.devices_info.length > 0
                            ? order.devices_info.map((device, index) => (
                                <Grid item xs={12} sx={{ height: "20px" }} key={device.deviceId}>
                                  {device.deviceNumber}{" "}
                                  {device.financeInfo !== null &&
                                    device.financeInfo.length > 0 &&
                                    device.financeInfo.map((bill) => (
                                      <MDBadge
                                        key={bill.financeId}
                                        sx={{
                                          width: "13px",
                                          height: "13px",
                                          margin: "0px 2px 2px 0px",
                                          ".MuiBadge-badge": {
                                            minWidth: "13px",
                                            minHeight: "13px",
                                            borderRadius: "0.25rem",
                                            padding: "0px",
                                            margin: "0px",
                                            background:
                                              bill.status === "À facturer" &&
                                              "linear-gradient(195deg, #F2DA00, #D8C300)",
                                          },
                                        }}
                                        badgeContent={""}
                                        color={statusColor[bill.status]}
                                        variant="gradient"
                                        size="sm"
                                      />
                                    ))}
                                </Grid>
                              ))
                            : "Aucun appareil dans la commande"}
                        </MDTypography>
                      </Grid>
                      <Grid item xs={12}>
                        <MDTypography
                          mt={1.5}
                          sx={{ opacity: "0.7" }}
                          fontSize="0.75rem"
                          textTransform="uppercase"
                          textAlign="center"
                        >
                          Nom du chantier
                        </MDTypography>
                        <MDTypography mt={-0.7} variant="h6" textAlign="center">
                          {order.site_name ? order.site_name : "Nom du chantier inconnu"}
                        </MDTypography>
                      </Grid>
                      <Grid item xs={12}>
                        <MDTypography
                          mt={1.5}
                          sx={{ opacity: "0.7" }}
                          fontSize="0.75rem"
                          textTransform="uppercase"
                          textAlign="center"
                        >
                          Zone géographique
                        </MDTypography>
                        <MDTypography
                          mt={-0.7}
                          variant="h6"
                          /* style={{ color: colorDetermine(order && order.area) }} */
                          textAlign="center"
                          pb={2}
                        >
                          {order.area ? order.area : "Adresse inconnue"}
                        </MDTypography>
                      </Grid>
                    </Grid>
                    <Grid textAlign="center" pb={3} item container>
                      <Grid item xs={12}>
                        <Link to={`/commandes/${encodeURIComponent(order.order_id)}`}>
                          <MDButton
                            color={darkMode ? "white" : "dark"}
                            sx={{
                              width: 170,
                              padding: "6px 0 !important",
                              color: !darkMode ? "white" : "#1A2035",
                              boxShadow:
                                darkMode &&
                                "0rem 0.1875rem 0.1875rem 0rem rgba(255, 255, 255, 0.15), 0rem 0.1875rem 0.0625rem -0.125rem rgba(255, 255, 255, 0.2), 0rem 0.0625rem 0.3125rem 0rem rgba(255, 255, 255, 0.15)",
                              "&:hover": {
                                boxShadow:
                                  darkMode &&
                                  "0rem 0.875rem 1.625rem -0.75rem rgba(255, 255, 255, 0.4), 0rem 0.25rem 1.4375rem 0rem rgba(255, 255, 255, 0.15), 0rem 0.5rem 0.625rem -0.3125rem rgba(255, 255, 255, 0.2)",
                              },
                            }}
                          >
                            Gérer la commande
                          </MDButton>
                        </Link>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))
            ) : (
              orders.map((order) => (
                <Grid
                  item
                  container
                  xs={12}
                  key={order.order_id}
                  sx={{ paddingTop: "0px !important", marginBottom: "0px !important" }}
                >
                  <Grid item mt={0.5} mb={0.5} xs={12}>
                    <Divider
                      sx={{
                        margin: "0.5rem 0rem",
                        background: !darkMode ? "#000" : "#FFF",
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    container
                    sx={{ alignItems: "center", display: "flex", justifyContent: "center" }}
                    spacing={2}
                  >
                    <Grid item lg={3.1} xs={12}>
                      <MDTypography
                        variant="h5"
                        textAlign="center"
                        justifyContent="center"
                        sx={{ color: areaColor[order.area] }}
                      >
                        Commande {order.order_number ? order.order_number : "inconnu"}
                      </MDTypography>
                    </Grid>
                    <Grid item lg={2} xs={12}>
                      <MDTypography
                        mt={0.75}
                        sx={{ opacity: "0.7" }}
                        fontSize="0.75rem"
                        textTransform="uppercase"
                        textAlign="center"
                      >
                        Dernière modification
                      </MDTypography>
                      <MDTypography mt={-0.7} variant="h6" textAlign="center">
                        {order.last_edits.lastEdit
                          ? formatTimestamp(order.last_edits.lastEdit)
                          : "Date et heure inconnue"}
                      </MDTypography>
                      <MDTypography
                        mt={-0.5}
                        sx={{ opacity: "0.7" }}
                        fontSize="0.75rem"
                        textTransform="uppercase"
                        textAlign="center"
                      >
                        Par{" "}
                        <b>
                          {order.last_edits.userName !== null
                            ? order.last_edits.userName
                            : "utilisateur inconnu"}
                        </b>
                      </MDTypography>
                    </Grid>
                    <Grid item lg={2} xs={12}>
                      <MDTypography
                        mt={0.75}
                        sx={{ opacity: "0.7" }}
                        fontSize="0.75rem"
                        textTransform="uppercase"
                        textAlign="center"
                      >
                        Appareils de la commande
                      </MDTypography>
                      <MDTypography mt={-0.7} variant="h6" textAlign="center">
                        {order.devices_info !== null && order.devices_info.length > 0
                          ? order.devices_info.map((device, index) => (
                              <Grid item xs={12} sx={{ height: "20px" }} key={device.deviceId}>
                                {device.deviceNumber}{" "}
                                {device.financeInfo !== null &&
                                  device.financeInfo.length > 0 &&
                                  device.financeInfo.map((bill) => (
                                    <HtmlTooltip
                                      key={darkMode + bill.financeId}
                                      darkMode={darkMode} // Assuming you have a state or context to determine if it's dark mode
                                      title={
                                        <MDBox
                                          sx={{
                                            background: darkMode
                                              ? "white !important"
                                              : "linear-gradient(195deg, #364c70, #273956) !important",
                                            color: darkMode
                                              ? "#202940 !important"
                                              : "white !important",
                                            paddingBottom: 0.5,
                                          }}
                                        >
                                          <MDTypography
                                            key={darkMode + bill.financeId}
                                            variant="p"
                                            color={darkMode ? "#202940" : "white"}
                                          >
                                            <b>{bill.status}</b>
                                            <br />
                                            {bill.situationDescription}
                                            <br />
                                            {bill.requiredPercentage !== null &&
                                              `${bill.requiredPercentage}%`}
                                          </MDTypography>
                                        </MDBox>
                                      }
                                    >
                                      <MDBadge
                                        key={bill.financeId}
                                        sx={{
                                          width: "13px",
                                          height: "13px",
                                          margin: "0px 2px 2px 0px",
                                          ".MuiBadge-badge": {
                                            minWidth: "13px",
                                            minHeight: "13px",
                                            borderRadius: "0.25rem",
                                            padding: "0px",
                                            margin: "0px",
                                            background:
                                              bill.status === "À facturer" &&
                                              "linear-gradient(195deg, #F2DA00, #D8C300)",
                                          },
                                        }}
                                        badgeContent={""}
                                        color={statusColor[bill.status]}
                                        variant="gradient"
                                        size="sm"
                                      />
                                    </HtmlTooltip>
                                  ))}
                              </Grid>
                            ))
                          : "Aucun appareil dans la commande"}
                      </MDTypography>
                    </Grid>
                    <Grid item lg={2.9} xs={12}>
                      <MDTypography
                        mt={0.75}
                        sx={{ opacity: "0.7" }}
                        fontSize="0.75rem"
                        textTransform="uppercase"
                        textAlign="center"
                      >
                        Nom du chantier
                      </MDTypography>
                      <MDTypography mt={-0.7} variant="h6" textAlign="center">
                        {order.site_name ? order.site_name : "Nom du chantier inconnu"}
                      </MDTypography>
                    </Grid>
                    <Grid item lg={2} xs={12} textAlign="center">
                      <Link to={`/commandes/${encodeURIComponent(order.order_id)}`}>
                        <MDButton
                          size="small"
                          color={darkMode ? "white" : "dark"}
                          sx={{
                            width: 170,
                            padding: "6px 0 !important",
                            height: 40,
                            color: !darkMode ? "white" : "#1A2035",
                            boxShadow:
                              darkMode &&
                              "0rem 0.1875rem 0.1875rem 0rem rgba(255, 255, 255, 0.15), 0rem 0.1875rem 0.0625rem -0.125rem rgba(255, 255, 255, 0.2), 0rem 0.0625rem 0.3125rem 0rem rgba(255, 255, 255, 0.15)",
                            "&:hover": {
                              boxShadow:
                                darkMode &&
                                "0rem 0.875rem 1.625rem -0.75rem rgba(255, 255, 255, 0.4), 0rem 0.25rem 1.4375rem 0rem rgba(255, 255, 255, 0.15), 0rem 0.5rem 0.625rem -0.3125rem rgba(255, 255, 255, 0.2)",
                            },
                          }}
                        >
                          Gérer la commande
                        </MDButton>
                      </Link>
                    </Grid>
                  </Grid>
                </Grid>
              ))
            )
          ) : (
            <Grid item xs={12} justifyContent="center">
              <MDTypography pb={2} variant="h5" textAlign="center">
                Aucune commande à afficher
              </MDTypography>
            </Grid>
          )}
          {!isLoading && totalPages > 0 && (
            <Grid item container xs={12} justifyContent="center">
              <Stack spacing={2}>
                <Pagination
                  page={page}
                  onChange={handlePageChange}
                  variant="text"
                  size="large"
                  showFirstButton
                  showLastButton
                  sx={{
                    button: {
                      color: !darkMode ? "#1A2035" : "#FFF",
                    },
                    "button.Mui-selected": { color: !darkMode ? "#FFF" : "#1A2035" },
                  }}
                  count={totalPages}
                  color={darkMode ? "white" : "dark"}
                />
              </Stack>
            </Grid>
          )}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}
